'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "render", "style", "classNames", "tickRange"];
import * as React from 'react';
import { useStore } from '@base-ui/utils/store';
import { useRenderElement } from '@mui/x-scheduler-internals/base-ui-copy';
import { isWeekend } from '@mui/x-scheduler-internals/use-adapter';
import { useAdapterContext } from '@mui/x-scheduler-internals/use-adapter-context';
import { schedulerPreferenceSelectors } from '@mui/x-scheduler-internals/scheduler-selectors';
import { useEventTimelinePremiumStoreContext } from "../../use-event-timeline-premium-store-context/index.mjs";
import { eventTimelinePremiumPresetSelectors } from "../../event-timeline-premium-selectors/index.mjs";
import { iterate } from "./iterate.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const TimelineGridHeader = /*#__PURE__*/React.forwardRef(function TimelineGridHeader(componentProps, forwardedRef) {
  const {
      // Rendering props

      classNames,
      tickRange
      // Props forwarded to the DOM element
    } = componentProps,
    elementProps = _objectWithoutPropertiesLoose(componentProps, _excluded);
  const adapter = useAdapterContext();
  const store = useEventTimelinePremiumStoreContext();
  const {
    start,
    end,
    headers,
    timeResolution
  } = useStore(store, eventTimelinePremiumPresetSelectors.config);
  const ampm = useStore(store, schedulerPreferenceSelectors.ampm);
  const weekStartsOn = useStore(store, schedulerPreferenceSelectors.weekStartsOn);
  const children = headers.map((level, levelIndex) => {
    const allCells = iterate(adapter, level.unit, timeResolution, start, end, weekStartsOn);
    let cells;
    let offsetInTicks = 0;
    if (tickRange) {
      const filtered = filterCellsByTickRange(allCells, tickRange.firstTickIndex, tickRange.lastTickIndex);
      cells = filtered.visibleCells;
      offsetInTicks = filtered.offsetInTicks;
    } else {
      cells = allCells;
    }
    return /*#__PURE__*/_jsxs("div", {
      className: classNames?.row,
      "data-level": levelIndex,
      children: [offsetInTicks > 0 && /*#__PURE__*/_jsx("div", {
        role: "none",
        style: {
          width: `calc(var(--unit-width) * ${offsetInTicks})`
        }
      }), cells.map(cell => /*#__PURE__*/_jsx("div", {
        className: [classNames?.cell, level.className].filter(Boolean).join(' ') || undefined,
        "data-level": levelIndex,
        "data-unit": level.unit,
        "data-index": cell.index,
        "data-unit-leaf": level.unit === timeResolution ? '' : undefined,
        "data-weekend": level.unit === 'day' && isWeekend(adapter, cell.date) ? '' : undefined,
        style: {
          '--span': cell.spanInTicks
        },
        children: /*#__PURE__*/_jsx("time", {
          className: classNames?.label,
          dateTime: adapter.formatByString(cell.date, "yyyy-MM-dd'T'HH:mm"),
          children: level.renderCell ? level.renderCell({
            adapter,
            ampm,
            date: cell.date,
            start: cell.start,
            end: cell.end,
            index: cell.index,
            key: cell.key,
            level: levelIndex,
            spanInTicks: cell.spanInTicks,
            unit: level.unit
          }) : level.formatDate(adapter, cell.date)
        })
      }, cell.key))]
    }, `${level.unit}:${levelIndex}`);
  });
  return useRenderElement('div', componentProps, {
    ref: [forwardedRef],
    props: [elementProps, {
      children
    }]
  });
});
if (process.env.NODE_ENV !== "production") TimelineGridHeader.displayName = "TimelineGridHeader";
/**
 * Filters a level's cells to those overlapping a tick range and computes
 * the tick offset of the first visible cell within that level.
 */
function filterCellsByTickRange(cells, firstTickIndex, lastTickIndex) {
  let tickCursor = 0;
  let firstVisibleIdx = -1;
  let lastVisibleIdx = -1;
  for (let i = 0; i < cells.length; i += 1) {
    const cellEnd = tickCursor + cells[i].spanInTicks;
    // Cell overlaps [firstTickIndex, lastTickIndex) if cellEnd > first && tickCursor < last
    if (cellEnd > firstTickIndex && tickCursor < lastTickIndex) {
      if (firstVisibleIdx === -1) {
        firstVisibleIdx = i;
      }
      lastVisibleIdx = i;
    }
    tickCursor = cellEnd;
  }
  if (firstVisibleIdx === -1) {
    return {
      visibleCells: [],
      offsetInTicks: 0
    };
  }

  // Offset = tick position of the first visible cell
  let offsetInTicks = 0;
  for (let i = 0; i < firstVisibleIdx; i += 1) {
    offsetInTicks += cells[i].spanInTicks;
  }
  return {
    visibleCells: cells.slice(firstVisibleIdx, lastVisibleIdx + 1),
    offsetInTicks
  };
}